"use client";

import * as React from "react";

import { AdBand } from "@/components/shared/main/ad-band";
import { GAM_CODE_PREFIX, GAM_ENV, videoUnitPath } from "@/lib/ads/config";
import { buildTargeting, readAdTest, readChannel, readTier, readUserType } from "@/lib/ads/targeting";
import { useAdRuntime } from "./ad-provider";
import { loadIma } from "./ima-loader";
import { buildImaTag, videoTargetingFromDom } from "./video-ad-tag";

/**
 * In-article outstream video (client checklist 2026-09-16): a 16:9 box in the story that asks
 * the brand's `<Brand>_Web_Outstream` VIDEO unit for a VAST ad through the IMA SDK once it is
 * a slice of the box is in view, plays it muted (autoplay parity with the legacy JW schedule,
 * `plcmt=2`), pauses out of view, resumes on the way back, and offers tap-to-unmute.
 * Teads inRead, the legacy unit: when the video finishes or the viewer skips it, the
 * player leaves the article and the copy closes up. Scrolling away during playback
 * only pauses. No content video: the IMA display container owns the box. The 6x6
 * InRead unit stays available for vendor tags at the end of the body.
 *
 * Data attributes follow the display slots so the inventory harness sees the position; it is
 * not a GPT slot (video units answer VAST, not the display request).
 */
export function OutstreamSlot({ label = "廣告" }: { label?: string }) {
  const rt = useAdRuntime();
  const live = rt.enabled && !rt.pilot;
  const brand = rt.brand;
  const code = `${GAM_CODE_PREFIX}${brand}_Web_Outstream`;
  const path = videoUnitPath(brand, "Outstream");
  const boxRef = React.useRef<HTMLDivElement>(null);
  const adRef = React.useRef<HTMLDivElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [state, setState] = React.useState<"pending" | "filled" | "empty">("pending");
  const [muted, setMuted] = React.useState(true);
  const st = React.useRef<{ started: boolean; tries: number; done: boolean; manager?: any; loader?: any; display?: any }>({ started: false, tries: 0, done: false });
  const mutedRef = React.useRef(true);
  const targeting = rt.targeting;

  React.useEffect(() => {
    const box = boxRef.current, adDiv = adRef.current, content = videoRef.current;
    if (!live || !box || !adDiv || !content) return;
    const s = st.current;
    let visible = false;
    let cancelled = false;
    let pauseTimer = 0;
    // A failed request must not take the box down. The previous path treated an
    // error like a finished ad and removed the band once the reader scrolled,
    // so coming back found nothing and the 影片廣告 line was all they had seen.
    const videos = () => [...box.querySelectorAll("video")];
    const adVideo = () => videos().find((v) => v.videoWidth > 0) || videos().find((v) => v.currentSrc);
    const paint = () => {
      if (s.done) return;
      const v = adVideo();
      if (v && (v.currentTime > 0 || v.readyState >= 2)) setState("filled");
    };
    // IMA inserts its own video. The spare one is created unmuted at 0×0, and a
    // browser that refuses unmuted autoplay then tears down the ad that was
    // actually playing. Mute every element before asking it to play.
    const nudge = () => {
      if (cancelled || s.done || !visible) return;
      for (const v of videos()) {
        v.playsInline = true;
        v.setAttribute("playsinline", "");
        if (mutedRef.current || v.clientWidth === 0) v.muted = true;
      }
      const v = adVideo();
      if (!v) return;
      paint();
      if (!v.paused) return;
      if (mutedRef.current) v.muted = true;
      try { s.manager?.resume?.(); } catch { /* ignore */ }
      void v.play().catch(() => {});
    };
    const fail = (why: string) => {
      if (cancelled) return;
      console.info(`[ads] IMA Outstream ${why}`);
      try { s.manager?.destroy?.(); } catch { /* ignore */ }
      try { s.display?.destroy?.(); } catch { /* ignore */ }
      s.manager = undefined;
      s.display = undefined;
      s.loader = undefined;
      s.started = false;
      // Still on screen: ask again. Waiting for the next scroll left the
      // 影片廣告 line up for the whole time the reader was looking at it.
      if (visible && s.tries < 3) void start();
    };
    const start = async () => {
      if (cancelled || s.started || s.tries >= 3 || s.done) return;
      s.started = true;
      s.tries += 1;
      const ok = await loadIma();
      if (cancelled || s.manager) return;
      const ima = window.google?.ima;
      if (!ok || !ima || !adRef.current || !videoRef.current) { fail("unavailable (SDK blocked)"); return; }
      const kv = { ...buildTargeting(targeting, readUserType(), readChannel(), GAM_ENV || undefined, readTier(), readAdTest()), ...videoTargetingFromDom() };
      const tag = buildImaTag(brand, "Outstream", kv, { descriptionUrl: window.location.href.split("#")[0] });
      if (!tag) { fail("no tag"); return; }
      console.info("[ads] IMA Outstream request", tag);
      // IMA will not send the tag itself on an http page (error 1005, no request
      // at all) even though the browser can read it. Hand it the XML.
      let vast = "";
      try {
        const res = await fetch(tag);
        if (res.ok) vast = await res.text();
      } catch (err) { console.warn("[ads] IMA Outstream tag fetch failed", err); }
      if (cancelled || s.manager) return;
      content.muted = true;
      content.playsInline = true;
      content.setAttribute("playsinline", "");
      s.display = new ima.AdDisplayContainer(adDiv, content);
      s.display.initialize();
      s.loader = new ima.AdsLoader(s.display);
      const w = box.clientWidth || 640, h = box.clientHeight || Math.round((box.clientWidth || 640) * 9 / 16);
      s.loader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, (e: any) => {
        try { const er = e?.getError?.(); console.warn(`[ads] IMA Outstream error ${er?.getErrorCode?.()} (vast ${er?.getVastErrorCode?.()}): ${er?.getMessage?.()}`); } catch { /* diagnostics only */ }
        fail("error");
      }, false);
      s.loader.addEventListener(ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, (e: any) => {
        if (cancelled) return;
        const settings = new ima.AdsRenderingSettings();
        settings.enablePreloading = true;
        settings.uiElements = [ima.UiElements.AD_ATTRIBUTION, ima.UiElements.COUNTDOWN];
        const manager = e.getAdsManager(content, settings);
        s.manager = manager;
        // Teads inRead removes the player once the view is over. A failed request
        // must not take this path: the reader would lose the box before any frame.
        const finish = (why: string) => {
          if (s.done || cancelled) return;
          s.done = true;
          console.info(`[ads] IMA Outstream ${why}`);
          setState("empty");
          try { s.manager?.destroy?.(); } catch { /* ignore */ }
          try { s.display?.destroy?.(); } catch { /* ignore */ }
          s.manager = undefined;
        };
        manager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, () => fail("error"));
        // Do not pause here. A layout shift while the tag loads reports the box
        // off-screen for one frame; pausing inside STARTED froze the first
        // (black) frame and resume never drew the picture.
        manager.addEventListener(ima.AdEvent.Type.STARTED, () => { console.info("[ads] IMA Outstream ad started"); setState("filled"); nudge(); });
        manager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, () => finish("complete"));
        manager.addEventListener(ima.AdEvent.Type.COMPLETE, () => finish("complete"));
        manager.addEventListener(ima.AdEvent.Type.SKIPPED, () => finish("skipped"));
        try {
          manager.init(w, h, ima.ViewMode.NORMAL);
          manager.setVolume(mutedRef.current ? 0 : 1);
          manager.start();
          nudge();
        } catch (err) { console.warn("[ads] IMA Outstream init failed", err); fail("error"); }
      }, false);
      const req = new ima.AdsRequest();
      if (vast.includes("<Ad")) req.adsResponse = vast;
      else req.adTagUrl = tag;
      req.linearAdSlotWidth = w;
      req.linearAdSlotHeight = h;
      req.setAdWillAutoPlay(true);
      req.setAdWillPlayMuted(true);
      s.loader.requestAds(req);
    };
    const show = (isVis: boolean) => {
      visible = isVis;
      if (isVis) {
        if (pauseTimer) { window.clearTimeout(pauseTimer); pauseTimer = 0; }
        void start();
        nudge();
        return;
      }
      // The chart under the video scrolls itself into view while the tag is
      // loading. Pausing on that one jump left a black box. Wait, and only
      // pause once the reader has actually left the unit.
      if (!pauseTimer) pauseTimer = window.setTimeout(() => {
        pauseTimer = 0;
        if (visible || cancelled || s.done) return;
        try { s.manager?.pause?.(); } catch { /* ignore */ }
      }, 700);
    };
    const io = typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver((es) => {
          // A tenth of the box is enough to start. Half of a 16:9 column on a
          // tablet sits below the fold when the top of the video is already
          // on screen, so the spot never began while the reader was looking at it.
          const ratio = es.reduce((max, entry) => Math.max(max, entry.intersectionRatio), 0);
          show(ratio > 0);
        }, { threshold: [0, 0.1] })
      : null;
    if (io) io.observe(box); else show(true);
    // The bridge iframe covers the box once the ad exists, so this only
    // reaches a tap that lands before that. It still has to run inside the
    // gesture: a phone will not start IMA from the scroll callback.
    const tap = () => {
      try { s.display?.initialize(); } catch { /* ignore */ }
      const v = adVideo();
      const advancing = Boolean(v && !v.paused && v.currentTime > 0.2);
      if (!advancing) { s.started = false; s.tries = Math.min(s.tries, 1); void start(); }
      nudge();
    };
    box.addEventListener("pointerdown", tap);
    const ro = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => { const ima = window.google?.ima; if (s.manager && ima) { try { s.manager.resize(box.clientWidth, box.clientHeight, ima.ViewMode.NORMAL); } catch { /* ignore */ } } })
      : null;
    ro?.observe(box);
    const kick = window.setInterval(nudge, 500);
    return () => {
      cancelled = true;
      window.clearInterval(kick);
      if (pauseTimer) window.clearTimeout(pauseTimer);
      io?.disconnect();
      ro?.disconnect();
      box.removeEventListener("pointerdown", tap);
      try { s.manager?.destroy?.(); } catch { /* ignore */ }
      try { s.display?.destroy?.(); } catch { /* ignore */ }
      s.manager = undefined;
      s.display = undefined;
      s.loader = undefined;
      s.started = false;
      s.done = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, brand]);

  const stateAttr = rt.preview ? "preview" : !live ? "fallback" : state;
  const toggleMute = () => {
    const m = st.current.manager;
    const next = !muted;
    mutedRef.current = next;
    try { m?.setVolume(next ? 0 : 1); } catch { /* ignore */ }
    for (const v of adRef.current?.querySelectorAll("video") ?? []) {
      if (v.clientWidth > 0) v.muted = next;
    }
    setMuted(next);
  };

  return (
    <div className="hkej-ad-band-optional" data-ad-outstream="" hidden={state === "empty" && !rt.preview}>
      <AdBand label={label}>
        <div
          ref={boxRef}
          className="hkej-ad hkej-ad-outstream w-full"
          data-ad-format="Outstream"
          data-ad-slot={code}
          data-ad-unit={path}
          data-ad-sizes="d:640x480v;t:640x480v;m:640x480v"
          data-ad-state={stateAttr}
          style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", background: rt.preview ? undefined : "#000", overflow: "hidden" }}
        >
          {rt.preview ? (
            <div data-ad-preview="" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, border: "1px dashed #0d6b4b", background: "rgba(13,107,75,.06)", color: "#0d6b4b", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11, textAlign: "center" }}>
              <strong style={{ fontWeight: 600 }}>{code}</strong>
              <span>outstream video 16:9 · 640x480v</span>
              <span style={{ opacity: 0.7 }}>muted autoplay once in view · closes when the video ends</span>
            </div>
          ) : null}
          <video
            ref={videoRef}
            muted
            playsInline
            preload="none"
            aria-hidden
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
          />
          <div ref={adRef} style={{ position: "absolute", inset: 0 }} />
          {state === "pending" && live && !rt.preview ? (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, letterSpacing: "0.08em", pointerEvents: "none" }}>
              影片廣告
            </div>
          ) : null}
          {state === "filled" ? (
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "開啟聲音" : "靜音"}
              style={{ position: "absolute", right: 8, bottom: 8, zIndex: 2, height: 28, padding: "0 10px", fontSize: 12, lineHeight: "28px", color: "#fff", background: "rgba(0,0,0,.6)", border: "1px solid rgba(255,255,255,.4)", borderRadius: 4, cursor: "pointer" }}
            >
              {muted ? "🔇 開啟聲音" : "🔊 靜音"}
            </button>
          ) : null}
        </div>
      </AdBand>
    </div>
  );
}
